import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { cateBestApi, type CateBestOrderBy, type CateBestRow } from '../../api/cateBest.js';
import { Spinner, ErrorBox } from '../../components/ui.js';

/**
 * 노출메뉴관리(베스트) — 원본 zaiko/cate_best_list.php + cate_best_list_load_data.php.
 *
 * 원본 화면을 그대로 옮겼다 (2026-11-04 요청: "레거시와 완전 동일하게").
 *   - 제목, 검색 칸(초기화 · 카테고리명/코드 · 검색), 오른쪽 안내 세 줄과
 *     상태 배치의 업데이트 시간.
 *   - 컬럼: 번호 · 카테고리명 · 카테고리 · 재고상품수 · 베스트설정수 · 일반설정수.
 *     번호 말고는 머리칸을 눌러 정렬한다 - 같은 칸이면 방향만 뒤집고, 다른
 *     칸이면 오름차순부터. 첫 화면(orderby 빈 값)은 화살표 없이 코드순이다.
 *   - 카테고리명·코드는 쇼핑몰 카테고리 목록을 새 창으로 연다.
 *   - 재고상품수가 베스트설정수와 다른 줄은 빨갛게 굵게.
 *   - 무한 스크롤로 50건씩 이어붙인다. EXCLUDE_CODES 는 원본처럼 화면에서
 *     거르고, 번호도 거른 뒤의 순서로 1부터 센다.
 * 바탕색·표 테두리 같은 바탕 톤만 원본의 고정 다크 색 대신 앱 테마를 따른다.
 */

/** 원본 :344 - 목록에서 빼는 카테고리 (대분류·이벤트성 카테고리) */
const EXCLUDE_CODES = new Set(['03', '07', '0C', '0E', '0G', '0H', '0I', '0K', '0L', '0M', '0M02', '0N', '0P', '0Q', '0R']);

const ORDER_BY: CateBestOrderBy[] = ['catename', 'mcode', 'soldout_score', 'best_numper', 'numper'];

const SHOP_LIST_URL = 'https://www.bananamall.co.kr/shop/list.php?cate=';

interface Filters {
  searchvalue: string;
  orderby: CateBestOrderBy | '';
  order: 'ASC' | 'DESC';
}

export function CateBestPage() {
  const [params, setParams] = useSearchParams();

  const committed = useMemo<Filters>(() => {
    const orderby = params.get('orderby') ?? '';
    return {
      searchvalue: params.get('searchvalue') ?? '',
      orderby: (ORDER_BY as string[]).includes(orderby) ? (orderby as CateBestOrderBy) : '',
      order: params.get('order')?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC',
    };
  }, [params]);

  // 검색어는 [검색] 을 눌러야 반영된다 (원본도 폼 submit).
  const [draft, setDraft] = useState(committed.searchvalue);
  useEffect(() => setDraft(committed.searchvalue), [committed.searchvalue]);

  const list = useInfiniteQuery({
    queryKey: ['cate-best', committed],
    queryFn: ({ pageParam }) => cateBestApi.list({ ...committed, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => (last.items.length > 0 ? pages.length + 1 : undefined),
  });

  const rows: CateBestRow[] = (list.data?.pages.flatMap((p) => p.items) ?? []).filter(
    (r) => !EXCLUDE_CODES.has(r.mcode),
  );
  const updatedAt = list.data?.pages[0]?.updatedAt;
  const loading = list.isFetching || list.isFetchingNextPage;

  function apply(next: Partial<Filters>) {
    const merged = { ...committed, searchvalue: draft, ...next };
    const out = new URLSearchParams();
    if (merged.searchvalue) out.set('searchvalue', merged.searchvalue);
    out.set('orderby', merged.orderby);
    out.set('order', merged.order);
    setParams(out, { replace: true });
  }

  /** 원본 sortTable */
  function sortBy(column: CateBestOrderBy) {
    if (committed.orderby === column) apply({ orderby: column, order: committed.order === 'ASC' ? 'DESC' : 'ASC' });
    else apply({ orderby: column, order: 'ASC' });
  }

  /** 원본 resetSearch - 주소의 조건을 전부 지운다. */
  function reset() {
    setDraft('');
    setParams(new URLSearchParams(), { replace: true });
  }

  // 무한 스크롤 - 표 아래 감시 요소가 보이면 다음 50건.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) {
          void list.fetchNextPage();
        }
      },
      { rootMargin: '100px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.hasNextPage, list.isFetchingNextPage, rows.length]);

  return (
    <section className="cate-best-page">
      <header className="cate-best-head">
        <h1 className="cate-best-head__title">노출메뉴관리(베스트)</h1>
      </header>

      <div className="cate-best-top">
        <div className="cate-best-search">
          <form
            className="cate-best-search__form"
            onSubmit={(e) => {
              e.preventDefault();
              apply({});
            }}
          >
            <button type="button" className="btn btn--ghost" onClick={reset}>
              초기화
            </button>
            <label className="cate-best-search__label" htmlFor="cate-best-search">
              카테고리명/코드:
            </label>
            <input
              id="cate-best-search"
              className="input cate-best-search__input"
              value={draft}
              placeholder="검색어를 입력하세요..."
              onChange={(e) => setDraft(e.target.value)}
            />
            <button type="submit" className="btn btn--primary">
              검색
            </button>
          </form>
        </div>

        <div className="cate-best-info">
          <div>〇 매 10분마다 업데이트 됩니다.</div>
          <div>※ 스크롤을 내리면 데이터를 자동으로 로딩합니다.</div>
          <div>
            ※ 업데이트 시간: <strong>{updatedAt ?? ''}</strong>
          </div>
        </div>
      </div>

      {list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <div className="table-wrap">
          <table className="table cate-best-table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>번호</th>
                <SortHeader column="catename" label="카테고리명" committed={committed} onSort={sortBy} />
                <SortHeader column="mcode" label="카테고리" committed={committed} onSort={sortBy} />
                <SortHeader column="soldout_score" label="재고상품수" committed={committed} onSort={sortBy} />
                <SortHeader column="best_numper" label="베스트설정수" committed={committed} onSort={sortBy} />
                <SortHeader column="numper" label="일반설정수" committed={committed} onSort={sortBy} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.mcode} className={row.isWarning ? 'cate-best-row--warn' : undefined}>
                  <td className="cate-best__no">{i + 1}</td>
                  <td className="cate-best__name">
                    <a href={SHOP_LIST_URL + row.mcode} target="_blank" rel="noopener noreferrer">
                      {row.catename}
                    </a>
                  </td>
                  <td className="cate-best__code">
                    <a href={SHOP_LIST_URL + row.mcode} target="_blank" rel="noopener noreferrer">
                      {row.mcode}
                    </a>
                  </td>
                  <td>{row.soldoutScore}</td>
                  <td>{row.bestNumper}</td>
                  <td>{row.numper}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="cate-best__empty">
                    검색 결과가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div ref={sentinelRef} />
          {loading && <Spinner label="데이터를 불러오는 중입니다..." />}
        </div>
      )}
    </section>
  );
}

function SortHeader({
  column,
  label,
  committed,
  onSort,
}: {
  column: CateBestOrderBy;
  label: string;
  committed: Filters;
  onSort: (c: CateBestOrderBy) => void;
}) {
  const active = committed.orderby === column;
  return (
    <th aria-sort={active ? (committed.order === 'ASC' ? 'ascending' : 'descending') : undefined}>
      <button type="button" className="th-sort" onClick={() => onSort(column)}>
        {label}
        {active && (
          <span className="th-sort__arrow th-sort__arrow--on">{committed.order === 'ASC' ? '▲' : '▼'}</span>
        )}
      </button>
    </th>
  );
}
